import { ContentTypes, IOPS } from "./block-base";
import { BlockTypes } from "./block-types";
import { TrafficPatterns, TrafficRoutes, computeIOPSFromRoutes } from "./traffic-patterns";

export type ProblemDifficulty = "easy" | "medium" | "hard";

export const PROBLEM_DIFFICULTIES: ProblemDifficulty[] = ["easy", "medium", "hard"];

export const ProblemDifficultyNames: Record<ProblemDifficulty, string> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
};

export interface ProblemTraffic {
  pattern: TrafficPatterns;
  routes: TrafficRoutes;
  minTPS: number;
  maxTPS: number;
}

export interface InterviewProblem {
  id: string;
  title: string;
  difficulty: ProblemDifficulty;
  summary: string;
  tags: string[];
  functionalRequirements: string[];
  scaleTargets: string[];
  hints: string[];
  suggestedBlocks: BlockTypes[];
  traffic: ProblemTraffic;
}

export const PROBLEMS: InterviewProblem[] = [
  {
    id: "url-shortener",
    title: "URL Shortener",
    difficulty: "easy",
    summary: "Turn long URLs into short codes and redirect visitors fast.",
    tags: ["read-heavy", "key-value"],
    functionalRequirements: [
      "Create a short code for a long URL.",
      "Redirect a short code to its original URL.",
      "Codes never collide and never change once issued.",
    ],
    scaleTargets: [
      "Roughly 100 redirects for every new link.",
      "Traffic grows every level, so plan for 10x the starting load.",
    ],
    hints: [
      "Redirects read the same few popular codes over and over. What can answer them without touching the database?",
      "Writes are rare. A single primary database with something absorbing reads goes a long way.",
    ],
    suggestedBlocks: [BlockTypes.LOAD_BALANCER, BlockTypes.APP_SERVER, BlockTypes.CACHE, BlockTypes.DATABASE],
    traffic: {
      pattern: TrafficPatterns.INCREASE_BY_LEVEL,
      routes: [
        {
          id: "url-redirect",
          path: "/r/:code",
          weight: 95,
          contentTypes: [{ id: "url-redirect-dynamic", type: ContentTypes.DYNAMIC, weight: 100, readRatio: 99 }],
        },
        {
          id: "url-shorten",
          path: "/shorten",
          weight: 5,
          contentTypes: [{ id: "url-shorten-dynamic", type: ContentTypes.DYNAMIC, weight: 100, readRatio: 0 }],
        },
      ],
      minTPS: 50,
      maxTPS: 5000,
    },
  },
  {
    id: "pastebin",
    title: "Pastebin",
    difficulty: "easy",
    summary: "Let people store text snippets and share them by link.",
    tags: ["read-heavy", "static content"],
    functionalRequirements: [
      "Create a paste and get back a unique link.",
      "Anyone with the link can view the paste.",
      "Pastes are immutable once created.",
    ],
    scaleTargets: [
      "About 4 views for every new paste.",
      "Traffic rises and falls in waves through the day.",
    ],
    hints: [
      "A paste never changes after it is written. Content that never changes can be served from very close to the user.",
      "Keep the write path simple: an app server in front of a database is enough for new pastes.",
    ],
    suggestedBlocks: [BlockTypes.CDN, BlockTypes.S3_BUCKET, BlockTypes.APP_SERVER, BlockTypes.DATABASE],
    traffic: {
      pattern: TrafficPatterns.STEADY_WAVE,
      routes: [
        {
          id: "paste-view",
          path: "/p/:id",
          weight: 80,
          contentTypes: [{ id: "paste-view-static", type: ContentTypes.STATIC, weight: 100, readRatio: 100 }],
        },
        {
          id: "paste-create",
          path: "/paste",
          weight: 20,
          contentTypes: [{ id: "paste-create-dynamic", type: ContentTypes.DYNAMIC, weight: 100, readRatio: 0 }],
        },
      ],
      minTPS: 500,
      maxTPS: 1500,
    },
  },
  {
    id: "photo-sharing",
    title: "Photo Sharing (Instagram)",
    difficulty: "medium",
    summary: "Upload photos and browse a feed of images from people you follow.",
    tags: ["media", "read-heavy", "feed"],
    functionalRequirements: [
      "Upload a photo with a caption.",
      "View a feed of recent photos from followed accounts.",
      "Open any photo at full size.",
    ],
    scaleTargets: [
      "Image downloads make up almost half of all traffic.",
      "Traffic ramps from zero to peak as a new region launches.",
    ],
    hints: [
      "Image bytes and feed data have very different needs. Should they take the same path?",
      "Object storage is cheap for media, but a CDN in front of it keeps it from melting at peak.",
      "The feed is read far more than it is written. Cache it.",
    ],
    suggestedBlocks: [BlockTypes.CDN, BlockTypes.S3_BUCKET, BlockTypes.LOAD_BALANCER, BlockTypes.APP_SERVER, BlockTypes.CACHE, BlockTypes.NO_SQL_DB],
    traffic: {
      pattern: TrafficPatterns.RAMP,
      routes: [
        {
          id: "photo-feed",
          path: "/feed",
          weight: 50,
          contentTypes: [{ id: "photo-feed-dynamic", type: ContentTypes.DYNAMIC, weight: 100, readRatio: 95 }],
        },
        {
          id: "photo-image",
          path: "/img/:id",
          weight: 45,
          contentTypes: [{ id: "photo-image-media", type: ContentTypes.STATIC_MEDIA, weight: 100, readRatio: 100 }],
        },
        {
          id: "photo-upload",
          path: "/upload",
          weight: 5,
          contentTypes: [{ id: "photo-upload-media", type: ContentTypes.STATIC_MEDIA, weight: 100, readRatio: 0 }],
        },
      ],
      minTPS: 0,
      maxTPS: 8000,
    },
  },
  {
    id: "news-feed",
    title: "News Feed (Twitter Timeline)",
    difficulty: "medium",
    summary: "Show each user a timeline of posts from the accounts they follow.",
    tags: ["read-heavy", "feed", "back pressure"],
    functionalRequirements: [
      "Publish a short text post.",
      "Load a home timeline of recent posts from followed accounts.",
      "New posts appear in followers' timelines within seconds.",
    ],
    scaleTargets: [
      "Timeline reads outnumber posts more than 5 to 1.",
      "Load grows each level, and backs off when your system starts failing requests.",
    ],
    hints: [
      "Building a timeline on every read is expensive. Could timelines be precomputed and cached?",
      "Posting doesn't have to update every follower synchronously. Something can buffer that work.",
    ],
    suggestedBlocks: [BlockTypes.LOAD_BALANCER, BlockTypes.APP_SERVER, BlockTypes.CACHE, BlockTypes.QUEUE, BlockTypes.NO_SQL_DB],
    traffic: {
      pattern: TrafficPatterns.INCREASE_BY_LEVEL_WITH_BACK_PRESSURE,
      routes: [
        {
          id: "feed-timeline",
          path: "/timeline",
          weight: 85,
          contentTypes: [{ id: "feed-timeline-dynamic", type: ContentTypes.DYNAMIC, weight: 100, readRatio: 100 }],
        },
        {
          id: "feed-post",
          path: "/post",
          weight: 15,
          contentTypes: [{ id: "feed-post-dynamic", type: ContentTypes.DYNAMIC, weight: 100, readRatio: 0 }],
        },
      ],
      minTPS: 100,
      maxTPS: 10000,
    },
  },
  {
    id: "video-streaming",
    title: "Video Streaming (YouTube)",
    difficulty: "hard",
    summary: "Serve video to a global audience while creators keep uploading.",
    tags: ["media", "bandwidth", "global"],
    functionalRequirements: [
      "Creators upload videos.",
      "Viewers stream any video with minimal buffering.",
      "Viewers see video metadata: title, views, and recommendations.",
    ],
    scaleTargets: [
      "90% of traffic is streaming video bytes.",
      "Traffic ramps to a very high peak; origin servers alone cannot keep up.",
    ],
    hints: [
      "Video segments are static once encoded. Push them as close to viewers as you can.",
      "Metadata is small and mostly read. It needs a very different path from video bytes.",
      "Uploads are slow and bursty. Decouple them from the rest of the system.",
    ],
    suggestedBlocks: [BlockTypes.CDN, BlockTypes.S3_BUCKET, BlockTypes.LOAD_BALANCER, BlockTypes.APP_SERVER, BlockTypes.QUEUE, BlockTypes.CACHE, BlockTypes.DATABASE],
    traffic: {
      pattern: TrafficPatterns.RAMP,
      routes: [
        {
          id: "video-watch",
          path: "/watch",
          weight: 90,
          contentTypes: [{ id: "video-watch-media", type: ContentTypes.STATIC_MEDIA, weight: 100, readRatio: 100 }],
        },
        {
          id: "video-meta",
          path: "/meta",
          weight: 8,
          contentTypes: [{ id: "video-meta-dynamic", type: ContentTypes.DYNAMIC, weight: 100, readRatio: 90 }],
        },
        {
          id: "video-upload",
          path: "/upload",
          weight: 2,
          contentTypes: [{ id: "video-upload-media", type: ContentTypes.STATIC_MEDIA, weight: 100, readRatio: 0 }],
        },
      ],
      minTPS: 0,
      maxTPS: 50000,
    },
  },
  {
    id: "flash-sale-ticketing",
    title: "Flash-Sale Ticketing",
    difficulty: "hard",
    summary: "Sell a limited batch of concert tickets when everyone shows up at once.",
    tags: ["spiky traffic", "write-heavy", "queue"],
    functionalRequirements: [
      "Browse the event page and seat map.",
      "Check out and reserve seats.",
      "Never sell the same seat twice.",
    ],
    scaleTargets: [
      "Traffic spikes from a trickle to peak and back, over and over.",
      "Checkout is almost all writes, and the database cannot take the full spike.",
    ],
    hints: [
      "The event page is the same for everyone. Don't let it hit your servers.",
      "Checkouts don't all need to finish instantly. Something can absorb the spike and drain it at a steady rate.",
    ],
    suggestedBlocks: [BlockTypes.CDN, BlockTypes.LOAD_BALANCER, BlockTypes.APP_SERVER, BlockTypes.QUEUE, BlockTypes.DATABASE],
    traffic: {
      pattern: TrafficPatterns.PERIODIC,
      routes: [
        {
          id: "sale-event",
          path: "/event",
          weight: 70,
          contentTypes: [{ id: "sale-event-static", type: ContentTypes.STATIC, weight: 100, readRatio: 100 }],
        },
        {
          id: "sale-checkout",
          path: "/checkout",
          weight: 30,
          contentTypes: [{ id: "sale-checkout-dynamic", type: ContentTypes.DYNAMIC, weight: 100, readRatio: 10 }],
        },
      ],
      minTPS: 50,
      maxTPS: 6000,
    },
  },
];

export const getProblemById = (id: string | null | undefined): InterviewProblem | undefined =>
  id ? PROBLEMS.find((p) => p.id === id) : undefined;

/**
 * The root IOPS a problem starts with. Mirrors IOPSConfigModal's save logic:
 * steady/increase patterns start at minTPS, every other pattern is sized from maxTPS.
 */
export function getProblemInitialIOPS(traffic: ProblemTraffic): IOPS {
  const startsAtMin =
    traffic.pattern === TrafficPatterns.STEADY ||
    traffic.pattern === TrafficPatterns.INCREASE_BY_LEVEL ||
    traffic.pattern === TrafficPatterns.INCREASE_BY_LEVEL_WITH_BACK_PRESSURE;
  return computeIOPSFromRoutes(startsAtMin ? traffic.minTPS : traffic.maxTPS, traffic.routes);
}
